import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { 
  Mic, MicOff, Volume2, VolumeX, X, Send, 
  Loader2, Sparkles, User, Keyboard, MessageSquare
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VoiceChatBot = ({ isOpen, onClose, selectedBusiness }) => {
  const [messages, setMessages] = useState([]);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [sessionId] = useState(() => `voice_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [inputMode, setInputMode] = useState('voice'); // 'voice' or 'text'
  const [textInput, setTextInput] = useState('');
  
  const recognitionRef = useRef(null);
  const synthRef = useRef(window.speechSynthesis);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-GB';

      recognitionRef.current.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        setTranscript(finalTranscript || interimTranscript);

        if (finalTranscript) {
          handleSendMessage(finalTranscript);
          setTranscript('');
        }
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
      synthRef.current.cancel();
    };
  }, []);

  // Initialize chat with greeting (NO VOICE - text only)
  useEffect(() => {
    if (isOpen && !hasInitialized && selectedBusiness) {
      const greeting = `Hi, I'm Aria from NEURA AI! I'll help you book a FREE call to discuss your website or digital project. I just need 5 quick details. First, what's your business name?`;
      
      const initialMessage = {
        id: 1,
        role: 'assistant',
        content: greeting,
        timestamp: new Date()
      };
      
      setMessages([initialMessage]);
      setHasInitialized(true);
      
      // Voice disabled - text only chatbot
    }
  }, [isOpen, selectedBusiness, hasInitialized]);

  // Reset when closed
  useEffect(() => {
    if (!isOpen) {
      setHasInitialized(false);
      setMessages([]);
      setTranscript('');
      setTextInput('');
      setInputMode('voice');
      synthRef.current.cancel();
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
      setIsListening(false);
    }
  }, [isOpen]);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when switching to text mode
  useEffect(() => {
    if (inputMode === 'text' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputMode]);

  const speakText = useCallback((text) => {
    if (isMuted || !synthRef.current) return;

    synthRef.current.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    // Female voice - confident, slightly slower for clarity
    utterance.rate = 0.95; // Slower, clearer pace
    utterance.pitch = 1.1; // Slightly higher for confident female voice
    utterance.volume = 1;
    utterance.lang = 'en-US'; // American English for clarity
    
    // Get available voices and select the best female voice
    const voices = synthRef.current.getVoices();
    
    // Priority order for female voices
    const femaleVoice = voices.find(voice => 
      voice.name.includes('Google US English') && voice.name.includes('Female')
    ) || voices.find(voice => 
      voice.name.includes('Samantha') // macOS
    ) || voices.find(voice => 
      voice.name.includes('Google UK English Female')
    ) || voices.find(voice => 
      voice.name.includes('Microsoft Zira') // Windows
    ) || voices.find(voice => 
      voice.name.includes('Microsoft Eva') // Windows
    ) || voices.find(voice => 
      voice.name.includes('Karen') || // Australian
      voice.name.includes('Moira') || // Irish
      voice.name.includes('Tessa') || // South African
      voice.name.includes('Veena') || // Indian
      voice.name.includes('Victoria') ||
      voice.name.includes('Allison') ||
      voice.name.includes('Susan') ||
      voice.name.includes('Zoe') ||
      voice.name.includes('Nicky') ||
      voice.name.includes('Female')
    ) || voices.find(voice => 
      voice.lang.startsWith('en') && voice.name.toLowerCase().includes('female')
    ) || voices.find(voice =>
      voice.lang.startsWith('en')
    );
    
    if (femaleVoice) {
      utterance.voice = femaleVoice;
    }

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    synthRef.current.speak(utterance);
  }, [isMuted]);

  const toggleListening = () => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in your browser. Please use Chrome or Edge, or switch to typing mode.');
      setInputMode('text');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      synthRef.current.cancel();
      setTranscript('');
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  const handleSendMessage = async (text) => {
    if (!text.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: text.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setTextInput('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/chat/booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: text.trim(),
          session_id: sessionId,
          business_category: selectedBusiness?.name || null
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      
      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      // Voice disabled - text only chatbot
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: "My apologies, there's a connection issue. Please contact us directly at 074 7134 3190 or PeterG@neura-ai.info.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      // Voice disabled - text only chatbot
    } finally {
      setIsLoading(false);
    }
  };

  const handleTextSubmit = (e) => {
    e.preventDefault();
    if (textInput.trim()) {
      handleSendMessage(textInput);
    }
  };

  const toggleMute = () => {
    if (!isMuted) {
      synthRef.current.cancel();
    }
    setIsMuted(!isMuted);
  };

  const switchInputMode = () => {
    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
    setInputMode(inputMode === 'voice' ? 'text' : 'voice');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl mx-4 bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 p-6">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className={`w-16 h-16 rounded-full bg-white/20 flex items-center justify-center ${isSpeaking ? 'animate-pulse' : ''}`}>
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white ${isListening ? 'bg-red-500 animate-pulse' : 'bg-emerald-400'}`} />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-white">Aria</h2>
              <p className="text-white/80 text-sm">Professional Booking Assistant</p>
              {selectedBusiness && (
                <span className="inline-block mt-1 px-3 py-1 rounded-full bg-white/20 text-white text-xs">
                  {selectedBusiness.name}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Messages */}
        <ScrollArea className="h-[300px] p-6">
          <div className="space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
              >
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                  message.role === 'assistant' 
                    ? 'bg-gradient-to-br from-cyan-500 to-purple-500' 
                    : 'bg-gray-200'
                }`}>
                  {message.role === 'assistant' ? (
                    <Sparkles className="w-5 h-5 text-white" />
                  ) : (
                    <User className="w-5 h-5 text-gray-600" />
                  )}
                </div>
                <div className={`max-w-[75%] ${message.role === 'user' ? 'text-right' : ''}`}>
                  <div className={`rounded-2xl px-4 py-3 ${
                    message.role === 'assistant'
                      ? 'bg-gray-100 text-gray-700'
                      : 'bg-gradient-to-r from-cyan-500 to-purple-500 text-white'
                  }`}>
                    <p className="text-sm leading-relaxed">{message.content}</p>
                  </div>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div className="bg-gray-100 rounded-2xl px-4 py-3">
                  <Loader2 className="w-5 h-5 animate-spin text-purple-500" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Live Transcript (Voice Mode) */}
        {inputMode === 'voice' && transcript && (
          <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
            <p className="text-sm text-gray-500 italic">"{transcript}"</p>
          </div>
        )}

        {/* Input Controls */}
        <div className="p-6 bg-gray-50 border-t border-gray-100">
          {/* Mode Toggle */}
          <div className="flex justify-center mb-4">
            <div className="inline-flex bg-gray-200 rounded-full p-1">
              <button
                onClick={() => setInputMode('voice')}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  inputMode === 'voice' 
                    ? 'bg-white text-purple-600 shadow' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <Mic className="w-4 h-4" />
                Voice
              </button>
              <button
                onClick={() => setInputMode('text')}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  inputMode === 'text' 
                    ? 'bg-white text-purple-600 shadow' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <Keyboard className="w-4 h-4" />
                Type
              </button>
            </div>
          </div>

          {inputMode === 'voice' ? (
            /* Voice Controls */
            <>
              <div className="flex items-center justify-center gap-4">
                {/* Mute Button */}
                <Button
                  onClick={toggleMute}
                  variant="outline"
                  size="icon"
                  className={`w-12 h-12 rounded-full ${isMuted ? 'bg-gray-200' : 'bg-white'}`}
                >
                  {isMuted ? (
                    <VolumeX className="w-5 h-5 text-gray-500" />
                  ) : (
                    <Volume2 className="w-5 h-5 text-cyan-600" />
                  )}
                </Button>

                {/* Main Mic Button */}
                <button
                  onClick={toggleListening}
                  disabled={isLoading || isSpeaking}
                  className={`relative w-20 h-20 rounded-full flex items-center justify-center transition-all duration-300 ${
                    isListening
                      ? 'bg-red-500 scale-110 shadow-lg shadow-red-500/50'
                      : 'bg-gradient-to-r from-cyan-500 to-purple-500 hover:scale-105 shadow-lg shadow-purple-500/30'
                  } ${(isLoading || isSpeaking) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isListening ? (
                    <MicOff className="w-8 h-8 text-white" />
                  ) : (
                    <Mic className="w-8 h-8 text-white" />
                  )}
                  
                  {/* Listening Animation */}
                  {isListening && (
                    <>
                      <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-25" />
                      <span className="absolute inset-[-8px] rounded-full border-4 border-red-300 animate-pulse" />
                    </>
                  )}
                </button>

                {/* Switch to Text */}
                <Button
                  variant="outline"
                  size="icon"
                  className="w-12 h-12 rounded-full bg-white"
                  onClick={() => setInputMode('text')}
                >
                  <MessageSquare className="w-5 h-5 text-gray-500" />
                </Button>
              </div>

              <p className="text-center text-sm text-gray-500 mt-4">
                {isListening ? (
                  <span className="text-red-500 font-medium">Listening... Speak now</span>
                ) : isSpeaking ? (
                  <span className="text-purple-500 font-medium">Aria is speaking...</span>
                ) : (
                  'Tap the microphone to speak'
                )}
              </p>
            </>
          ) : (
            /* Text Input */
            <form onSubmit={handleTextSubmit} className="flex gap-2">
              <div className="flex-1 relative">
                <Input
                  ref={inputRef}
                  type="text"
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  placeholder="Type your message..."
                  className="pr-12 py-6 text-base"
                  disabled={isLoading}
                />
                {/* Mute toggle in text mode */}
                <button
                  type="button"
                  onClick={toggleMute}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600"
                >
                  {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </button>
              </div>
              <Button
                type="submit"
                disabled={!textInput.trim() || isLoading}
                className="px-6 bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoiceChatBot;
